import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDebouncedCallback } from "use-debounce";
import { MapPin, Search } from "lucide-react";
import { GlobalTextField } from "../../../../global/forms/GlobalFormFields";
import { searchAttendancePlaces } from "../../api/attendanceApi";
import ATTENDANCE_MAP_CONFIG from "../../config/mapConfig";

function buildPhotonDisplayName(p) {
  const parts = [p.name, p.street, p.district || p.suburb, p.city || p.town, p.state, p.country]
    .map((x) => String(x ?? "").trim())
    .filter(Boolean);
  return [...new Set(parts)].join(", ");
}

/** Client-side fallback when the API route is unreachable. */
async function searchPhotonDirect(query, { lat, lng } = {}) {
  const q = String(query ?? "").trim();
  if (q.length < 2) return [];

  const params = new URLSearchParams({ q, limit: "12", lang: "en" });
  if (lat != null && lng != null) {
    params.set("lat", String(lat));
    params.set("lon", String(lng));
    const d = 0.55;
    params.set("bbox", `${lng - d},${lat - d},${lng + d},${lat + d}`);
  }

  const base = ATTENDANCE_MAP_CONFIG.PHOTON_API_URL.replace(/\/?$/, "/");
  const res = await fetch(`${base}?${params.toString()}`);
  if (!res.ok) throw new Error("Search unavailable");
  const data = await res.json();
  return (data?.features ?? [])
    .map((feature) => {
      const coords = feature?.geometry?.coordinates;
      if (!coords?.length) return null;
      const p = feature.properties ?? {};
      const display_name = buildPhotonDisplayName(p);
      if (!display_name) return null;
      return {
        place_id: String(p.osm_id ?? `${coords[1]},${coords[0]}`),
        display_name,
        latitude: Number(coords[1]),
        longitude: Number(coords[0]),
      };
    })
    .filter(Boolean);
}

export default function PlaceSearchInput({
  label = "Search place",
  value,
  onChange,
  onPlaceSelect,
  disabled = false,
  nearLat = null,
  nearLng = null,
  cityHint = "",
}) {
  const [query, setQuery] = useState(value ?? "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchNote, setSearchNote] = useState("");
  const wrapRef = useRef(null);
  const inputWrapRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    setQuery(value ?? "");
  }, [value]);

  const updateDropdownPosition = () => {
    const el = inputWrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    updateDropdownPosition();
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, suggestions.length]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) {
        const portal = document.getElementById("att-place-suggestions-portal");
        if (portal?.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const runSearch = useDebouncedCallback(async (text) => {
    const q = String(text ?? "").trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSearchNote("");
      setLoading(false);
      setOpen(false);
      return;
    }

    const lat = nearLat != null ? Number(nearLat) : null;
    const lng = nearLng != null ? Number(nearLng) : null;
    const bias =
      Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : {};

    setLoading(true);
    setSearchNote("");
    try {
      let results = [];
      try {
        results = await searchAttendancePlaces(q, {
          ...bias,
          cityHint: cityHint || undefined,
        });
      } catch {
        results = await searchPhotonDirect(q, bias);
      }
      setSuggestions(results);
      setOpen(true);
      setSearchNote(
        results.length === 0
          ? cityHint
            ? `No results — try "${q}, ${cityHint}" or use My location / tap the map.`
            : 'No results — add your city (e.g. "Kakinada") or use My location on the map.'
          : "",
      );
      updateDropdownPosition();
    } catch {
      setSuggestions([]);
      setOpen(false);
      setSearchNote("Could not search places. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, 280);

  const handleInput = (next) => {
    setQuery(next);
    onChange?.(next);
    if (next.trim().length >= 2) {
      setLoading(true);
      setOpen(true);
      updateDropdownPosition();
      runSearch(next);
    } else {
      setSuggestions([]);
      setOpen(false);
      setSearchNote("");
      setLoading(false);
      runSearch.cancel();
    }
  };

  const handleSelect = (place) => {
    setQuery(place.display_name);
    onChange?.(place.display_name);
    onPlaceSelect?.(place);
    setSuggestions([]);
    setOpen(false);
    setSearchNote("");
  };

  const dropdown =
    open && (loading || suggestions.length > 0 || searchNote) ? (
      <ul
        id="att-place-suggestions-portal"
        className="attendance-place-search__suggestions attendance-place-search__suggestions--portal"
        role="listbox"
        style={{
          position: "fixed",
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
          zIndex: 10000,
        }}
      >
        {loading ? (
          <li className="attendance-place-search__status">
            <Search size={14} aria-hidden />
            Searching nearby places…
          </li>
        ) : null}
        {!loading && suggestions.length === 0 && searchNote ? (
          <li className="attendance-place-search__status">{searchNote}</li>
        ) : null}
        {!loading
          ? suggestions.map((place) => (
              <li key={`${place.place_id}-${place.latitude}-${place.longitude}`}>
                <button
                  type="button"
                  className="attendance-place-search__suggestion"
                  role="option"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(place)}
                >
                  <MapPin size={14} aria-hidden className="attendance-place-search__pin" />
                  <span>{place.display_name}</span>
                </button>
              </li>
            ))
          : null}
      </ul>
    ) : null;

  return (
    <div className="attendance-place-search" ref={wrapRef}>
      <div ref={inputWrapRef}>
        <GlobalTextField
          label={label}
          value={query}
          onChange={handleInput}
          disabled={disabled}
          placeholder={
            cityHint
              ? `e.g. Main road, ${cityHint}`
              : "Area, landmark, or street + city"
          }
          autoComplete="off"
        />
      </div>
      <p className="attendance-place-search__tip">
        {cityHint
          ? `Searching near ${cityHint}. Include the area name for local places.`
          : "Use My location first for better local results, or type place + city."}
      </p>
      {typeof document !== "undefined" && dropdown
        ? createPortal(dropdown, document.body)
        : null}
    </div>
  );
}
